import { useEffect, useState } from 'react'
import { displayTitle } from './displayName'

/**
 * Public discovery client — the unauthenticated landing/marketing read.
 *
 * These endpoints (`/v1/discover/*`) are the only catalog data reachable
 * without a session: the backend serves a bounded, field-allowlisted, cached
 * projection of popular dishes and verified chefs for the deployment's public
 * tenant (see internal/service/discovery). The landing page reads them so a
 * live build shows real backend content pre-auth, replacing the removed
 * client-side curated catalog. Everything else stays behind authentication.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

export interface PublicDish {
  chef_profile_id?: string
  allergens?: readonly string[]
  stock_quantity?: number

  id: string
  name: string
  image?: string
  price_minor: number
  currency: string
  category: string
  chef: string
  /** The vendor row, so a card can link to the kitchen's public page. */
  chef_id: string
  chef_avatar?: string
  /**
   * Trust signals. `reviews === 0` means nobody has reviewed this yet, which
   * the UI must say rather than render as a zero-star dish.
   */
  rating: number
  reviews: number
  description?: string
  ingredients?: readonly string[]
  cook_time_minutes?: number
  available_days?: readonly number[]
  menu_id?: string
  status?: string
  chef_badge?: string
  discount_percent?: number
}

export interface PublicPromo {
  id: string
  code: string
  name: string
  discount_percent: number
  min_subtotal_minor: number
  max_discount_minor: number
}

export interface PublicChef {
  id: string
  name: string
  cuisine: string
  area: string
  open: boolean
  /** Verification tier: verified | hygiene_certified | top_kitchen. */
  badge?: string
  avatar?: string
  rating: number
  reviews: number
  orders: number
  dish_count: number
  price_from_minor?: number
  vendor_type?: string
  /** Catalogue city of the kitchen nearest the caller ("gn:<id>"). */
  city_ref?: string
  /** "Kubwa, FCT". */
  city?: string
  /** Whole km, city centre to city centre; only when the caller named a city. */
  distance_km?: number
}

// Several mounted views read the same list at once (Home and Chefs both want
// `chefs?limit=50`); they share the request in flight instead of each sending one.
const inflight = new Map<string, Promise<unknown[]>>()

const fetchList = <T>(path: string, key: string): Promise<T[]> => {
  const pending = inflight.get(path)
  if (pending) return pending as Promise<T[]>
  const request = readList<T>(path, key).finally(() => inflight.delete(path))
  inflight.set(path, request)
  return request
}

const readList = async <T>(path: string, key: string): Promise<T[]> => {
  const resp = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Accept: 'application/json' },
    cache: 'no-cache',
    signal: AbortSignal.timeout(8000),
  })
  if (!resp.ok) {
    throw new Error(`Public discovery read ${path} failed with status ${resp.status}`)
  }
  const body = (await resp.json()) as Record<string, unknown>
  const list = body[key]
  return Array.isArray(list) ? (list as T[]) : []
}

// Names are display-normalised once, here, so every card built from the
// public feed reads the same way (see lib/displayName).
export const fetchPublicDishes = async (limit = 6): Promise<PublicDish[]> =>
  (await fetchList<PublicDish>(`/v1/discover/dishes?limit=${limit}`, 'dishes'))
    .map((dish) => ({ ...dish, name: displayTitle(dish.name), chef: dish.chef ? displayTitle(dish.chef) : dish.chef }))

/**
 * Verified chefs, nearest first when the caller says where they are.
 *
 * `where` is a catalogue city ref ("gn:2352778") or a typed area. A city ranks
 * kitchens by city-to-city distance; a typed area is resolved to a city by the
 * backend, falling back to a text match on the kitchen's label. Neither hides
 * a kitchen — only an explicit radius does, and this feed never sends one.
 * One string keeps it a single cache key in useFetched.
 */
export const fetchPublicChefs = (limit = 8, where = ''): Promise<PublicChef[]> => {
  const query = new URLSearchParams({ limit: String(limit) })
  const value = where.trim()
  if (value.startsWith('gn:')) query.set('city', value)
  else if (value) query.set('area', value)
  return fetchList<PublicChef>(`/v1/discover/chefs?${query.toString()}`, 'chefs')
    .then((chefs) => chefs.map((chef) => ({ ...chef, name: displayTitle(chef.name) })))
}

const discoverCache = new Map<string, readonly unknown[]>()

export const clearDiscoverCache = (): void => {
  discoverCache.clear()
}

export const refreshPublicCatalogue = (): void => {
  clearDiscoverCache()
  window.dispatchEvent(new Event('chow-catalogue-change'))
}

const useFetched = <T>(
  fetcher: (limit: number, area: string) => Promise<T[]>,
  limit: number,
  area = '',
): { data: readonly T[]; loading: boolean; error: boolean } => {
  const cacheKey = `${fetcher.name || 'discover'}:${limit}:${area}`
  const cached = discoverCache.get(cacheKey) as readonly T[] | undefined
  const [data, setData] = useState<readonly T[]>(() => cached ?? [])
  const [loading, setLoading] = useState<boolean>(() => cached === undefined)
  const [error, setError] = useState<boolean>(false)
  
  useEffect(() => {
    let active = true
    let version = 0
    let lastFetch = 0
    if (!discoverCache.has(cacheKey)) {
      setLoading(true)
    }
    const load = () => {
      const request = ++version
      lastFetch = Date.now()
      void fetcher(limit, area)
        .then((list) => {
          if (!active || request !== version) return
          discoverCache.set(cacheKey, list)
          setData(list)
          setError(false)
        })
        .catch(() => {
          // A rejected read must still settle the flag and preserve any
          // existing cached data (stale-while-revalidate), rather than wiping
          // valid rendered content to an empty screen.
          if (!active || request !== version) return
          setError(true)
          if (!discoverCache.has(cacheKey)) {
            setData([])
          }
        })
        .finally(() => {
          if (!active || request !== version) return
          setLoading(false)
        })
    }
    
    load()
    
    const onFocusOrVisible = () => {
      // Throttle revalidation to at least 30s
      if (document.visibilityState === 'visible' && Date.now() - lastFetch > 30_000) {
        load()
      }
    }
    
    window.addEventListener('chow-catalogue-change', load)
    document.addEventListener('visibilitychange', onFocusOrVisible)
    window.addEventListener('focus', onFocusOrVisible)
    
    return () => {
      active = false
      window.removeEventListener('chow-catalogue-change', load)
      document.removeEventListener('visibilitychange', onFocusOrVisible)
      window.removeEventListener('focus', onFocusOrVisible)
    }
  }, [fetcher, limit, area, cacheKey])
  
  return { data, loading, error }
}

/** Fetch popular dishes once on mount. `loading` is true until the first settle. */
export const usePublicDishes = (limit = 6): { dishes: readonly PublicDish[]; loading: boolean; error: boolean } => {
  const { data, loading, error } = useFetched(fetchPublicDishes, limit)
  return { dishes: data, loading, error }
}

/**
 * Fetch verified chefs, refetching when the customer's city or area changes.
 * `where` is a city ref or a typed area; see fetchPublicChefs.
 */
export const usePublicChefs = (
  limit = 8,
  where = '',
): { chefs: readonly PublicChef[]; loading: boolean; error: boolean } => {
  const { data, loading, error } = useFetched(fetchPublicChefs, limit, where)
  return { chefs: data, loading, error }
}

export const fetchPublicPromos = (): Promise<PublicPromo[]> =>
  fetchList<PublicPromo>('/v1/discover/promos', 'promos')

/** A published menu, as `GET /v1/discover/menus` states it. */
export interface PublicMenu {
  id: string
  title: string
  chef: string
  chef_id: string
  chef_avatar?: string
  badge: string
  area: string
  cover?: string
  dish_count: number
  open: boolean
}

export const fetchPublicMenus = (limit = 20): Promise<PublicMenu[]> =>
  fetchList<PublicMenu>(`/v1/discover/menus?limit=${limit}`, 'menus')

/**
 * Published menus with the in-flight flag for the read that feeds the page.
 *
 * The menus page renders these when they arrive and the projection otherwise, so
 * the loading state belongs to this hook rather than to the page reading it off a
 * synchronous snapshot — a flag there would always be false.
 */
export const usePublicMenus = (
  limit = 20,
): { menus: readonly PublicMenu[]; loading: boolean } => {
  const { data, loading } = useFetched(fetchPublicMenus, limit)
  return { menus: data, loading }
}

/** Fetch active public promotions. */
export const usePublicPromos = (): { promos: readonly PublicPromo[]; loading: boolean } => {
  const { data, loading } = useFetched(fetchPublicPromos, 5)
  return { promos: data, loading }
}

